/**
 * React state for a persona conversation.
 *
 * Holds the transcript the *user* sees. The authoritative transcript lives in
 * the Worker's Durable Object, keyed by session id — this is a local mirror for
 * rendering, and the two can legitimately diverge: a failed turn is never stored
 * server-side, so a message shown here as failed simply does not exist to the
 * persona. That is the intended behaviour, not a bug to paper over.
 */

import { useCallback, useMemo, useRef, useState } from "react";

import {
  PersonaApiError,
  createPersonaClient,
  type PersonaClient,
} from "../lib/persona-client";

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  /** True while tokens are still arriving for this message. */
  streaming?: boolean;
  /** True if the reply never arrived. Offers the retry affordance. */
  failed?: boolean;
}

export type ChatStatus = "idle" | "waiting" | "streaming" | "error";

export interface UsePersonaChatOptions {
  /** Provide a client, or a baseUrl to build one. */
  client?: PersonaClient;
  baseUrl?: string;
  /** Stream tokens as they generate. Off buffers the whole reply first. */
  stream?: boolean;
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function usePersonaChat(options: UsePersonaChatOptions = {}) {
  const { stream = true } = options;

  const client = useMemo(() => {
    if (options.client) return options.client;
    const baseUrl = options.baseUrl ?? import.meta.env.VITE_PERSONA_API_URL;
    if (!baseUrl) {
      throw new Error(
        "usePersonaChat needs a client or baseUrl. Set VITE_PERSONA_API_URL — see frontend/.env.example.",
      );
    }
    return createPersonaClient({ baseUrl });
  }, [options.client, options.baseUrl]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [error, setError] = useState<PersonaApiError | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // The last thing the user typed, so a failed turn can be retried without
  // asking them to type it again.
  const lastMessageRef = useRef<string>("");

  const patch = useCallback((id: string, changes: Partial<ChatMessage>) => {
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, ...changes } : message)),
    );
  }, []);

  const run = useCallback(
    async (text: string) => {
      const controller = new AbortController();
      abortRef.current = controller;

      const assistantId = newId();
      setMessages((current) => [
        ...current,
        { id: newId(), role: "user", content: text },
        { id: assistantId, role: "assistant", content: "", streaming: stream },
      ]);
      setStatus(stream ? "streaming" : "waiting");
      setError(null);

      try {
        if (stream) {
          await client.stream(text, {
            signal: controller.signal,
            onToken: (_token, accumulated) => patch(assistantId, { content: accumulated }),
          });
        } else {
          const { reply } = await client.send(text, { signal: controller.signal });
          patch(assistantId, { content: reply });
        }
        patch(assistantId, { streaming: false });
        setStatus("idle");
      } catch (cause) {
        // Cancelling is a user action, not a failure. Keep whatever tokens
        // arrived and return to idle rather than showing an error.
        if (cause instanceof DOMException && cause.name === "AbortError") {
          patch(assistantId, { streaming: false });
          setStatus("idle");
          return;
        }

        const apiError =
          cause instanceof PersonaApiError
            ? cause
            : new PersonaApiError("network_error", "Something went wrong.", 0);

        patch(assistantId, { streaming: false, failed: true });
        setError(apiError);
        setStatus("error");
      } finally {
        abortRef.current = null;
      }
    },
    [client, patch, stream],
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === "waiting" || status === "streaming") return;
      lastMessageRef.current = trimmed;
      void run(trimmed);
    },
    [run, status],
  );

  const retry = useCallback(() => {
    const text = lastMessageRef.current;
    if (!text) return;
    // Drop the failed exchange first: the server never stored it, so replaying
    // it would otherwise show the same question twice.
    setMessages((current) => current.slice(0, -2));
    void run(text);
  }, [run]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(async () => {
    abortRef.current?.abort();
    setMessages([]);
    setError(null);
    setStatus("idle");
    lastMessageRef.current = "";
    await client.reset();
  }, [client]);

  return {
    messages,
    status,
    error,
    /** Convenience for disabling the composer. */
    isBusy: status === "waiting" || status === "streaming",
    sessionId: client.getSessionId(),
    send,
    retry,
    cancel,
    reset,
  };
}
